/* The quote detail page's sections, and which of them is "current" (S-3).
 *
 * The page is one scrolling sheet of five sections under a sticky bar of links
 * to them. Which link the bar marks is decided HERE, as a pure function of the
 * section tops and the scroll position, so the rule is pinned by unit tests and
 * the browser plumbing that feeds it (`useSectionSpy`) carries no opinion of its
 * own.
 *
 * The keys are the page's, not the server's: nothing on the wire names a
 * section, and the page writes none of them to the URL — the old `?tab=` is
 * neither read nor written any more.
 */

/** The sections, in page order. `preview` exists only on a draft the reader
 *  may send; a page without it passes the first four. */
export const QUOTE_SECTIONS = ['client', 'route', 'pricing', 'terms', 'preview'] as const

export type QuoteSectionKey = (typeof QUOTE_SECTIONS)[number]

/** The `id` of a section element — what a link scrolls into view. */
export function sectionDomId(key: QuoteSectionKey): string {
  return `quote-${key}`
}

/** The `id` of a section's heading — what a link moves focus to. */
export function sectionHeadingId(key: QuoteSectionKey): string {
  return `${sectionDomId(key)}-heading`
}

export interface SpyView {
  /** How far the scroll container is scrolled. */
  scrollTop: number
  /** Its visible height. */
  viewportHeight: number
  /** Its whole content height. */
  scrollHeight: number
}

/** The current section.
 *
 * `sections` are in page order, each with its top measured in the scroll
 * container's CONTENT coordinates (the same space as `scrollTop`). `offset` is
 * the sticky bar's height: a section whose top is still behind the bar has not
 * been reached.
 *
 *   · the last section whose top has passed under the bar is current;
 *   · above the first one, the first is current;
 *   · scrolled to the very bottom, the LAST is current — a short final section
 *     can never bring its top under the bar, and its link would otherwise never
 *     light up however far the reader scrolled.
 *
 * The bottom rule applies only to a page that scrolls at all: one that fits
 * its viewport is "at the bottom" at rest, which says nothing about where the
 * reader is looking. One pixel of slack on both comparisons absorbs the
 * fractional tops a zoomed browser reports. */
export function spySection<K extends string>(
  sections: readonly { key: K; top: number }[],
  view: SpyView,
  offset: number,
): K | undefined {
  if (sections.length === 0) return undefined

  const scrollable = view.scrollHeight > view.viewportHeight + 1
  const atBottom = view.scrollTop + view.viewportHeight >= view.scrollHeight - 1
  if (scrollable && atBottom) return sections[sections.length - 1]!.key

  const line = view.scrollTop + offset + 1
  let current = sections[0]!.key
  for (const section of sections) {
    if (section.top <= line) current = section.key
  }
  return current
}

/** Where to scroll the container so a section's top lands just under the bar.
 *
 * Tops are viewport coordinates (`getBoundingClientRect().top`) of the section
 * and of the scroll container; `barHeight` is the strip the stuck bar covers
 * at the container's top edge. Never negative.
 *
 * This replaced `scrollIntoView`, which scrolls EVERY scrollable ancestor: the
 * document itself overflows by the top bar's height, so a section press also
 * scrolled <html> and dragged the whole shell — top bar, sidebar — up by it.
 * Only the spy's container moves now. The landing and `spySection` use the
 * same bar height, so the link pressed is the link lit once the scroll ends. */
export function landingScrollTop({
  sectionTop,
  containerTop,
  scrollTop,
  barHeight,
}: {
  sectionTop: number
  containerTop: number
  scrollTop: number
  barHeight: number
}): number {
  return Math.max(0, scrollTop + sectionTop - containerTop - barHeight)
}
